import { EventInput } from '@fullcalendar/core';
import { VisitorEvent } from '../../shared/models/visitor.event';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export type FullCalendarViewName =
  | 'dayGridMonth'
  | 'timeGridWeek'
  | 'timeGridDay'
  | 'listMonth';

export interface CalendarEventOccurrence {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs | null;
  startDay: dayjs.Dayjs;
  endDay: dayjs.Dayjs;
  isAllDay: boolean;
  isEndAllDay: boolean;
  sourceTime: VisitorEvent['eventTimes'][number];
}

export interface CalendarEventInput extends EventInput {
  extendedProps: {
    sourceEvent: VisitorEvent;
    occurrenceIndex?: number;
    segmentIndex?: number;
    isGroupedSpan?: boolean;
    dayGridTimeText?: string;
  };
}

export interface CalendarEventActiveRange {
  from: Date;
  to: Date;
}

interface CalendarEventSegment {
  occurrences: CalendarEventOccurrence[];
  startDay: dayjs.Dayjs;
  endDay: dayjs.Dayjs;
}

interface IndexedCalendarEventOccurrence {
  occurrence: CalendarEventOccurrence;
  occurrenceIndex: number;
}

const getOccurrenceTimezone = (event: VisitorEvent) =>
  event.timezoneFormatter || 'America/New_York';

const formatCalendarTimedDateTime = (value: dayjs.Dayjs) =>
  value.format('YYYY-MM-DDTHH:mm:ss');

const formatCalendarAllDayDate = (value: dayjs.Dayjs) =>
  value.format('YYYY-MM-DD');

export function getInitialFullCalendarView(isMobile: boolean): FullCalendarViewName {
  return isMobile ? 'listMonth' : 'dayGridMonth';
}

export function resolveCalendarViewFromDateRange(
  dateRange: string | undefined,
  from: Date | undefined,
  to: Date | undefined,
  fallback: string
): FullCalendarViewName {
  if (!dateRange || !from || !to) return fallback as FullCalendarViewName;

  switch (dateRange) {
    case 'today':
    case 'tomorrow':
      return 'timeGridDay';
    case 'this-week':
    case 'this-weekend':
      return 'timeGridWeek';
    case 'this-month':
      return 'dayGridMonth';
    case 'specific': {
      const dayCount = Math.max(1, dayjs(to).startOf('day').diff(dayjs(from).startOf('day'), 'day') + 1);
      if (dayCount <= 1) return 'timeGridDay';
      if (dayCount <= 7) return 'timeGridWeek';
      return 'dayGridMonth';
    }
    default:
      return fallback as FullCalendarViewName;
  }
}

export function buildCalendarEvents(
  events: VisitorEvent[],
  viewName: string,
  activeRange?: CalendarEventActiveRange | null
): CalendarEventInput[] {
  return events.flatMap((event) =>
    isDayGridView(viewName)
      ? buildDayGridEvents(event, activeRange)
      : buildOccurrenceEvents(event, activeRange)
  );
}

export function buildOccurrences(event: VisitorEvent): CalendarEventOccurrence[] {
  const timezoneFormatter = getOccurrenceTimezone(event);
  const times =
    event.eventTimes && event.eventTimes.length
      ? event.eventTimes
      : [{ startTime: event.startTime, endTime: event.endTime }];

  return times
    .filter((time) => !!time?.startTime)
    .map((time) => {
      const start = dayjs(time.startTime).tz(timezoneFormatter);
      const end = time.endTime ? dayjs(time.endTime).tz(timezoneFormatter) : null;
      // Use the explicit isAllDay property if available, otherwise check if time is midnight
      const isAllDay = time.isAllDay === true || (time.isAllDay == null && start.hour() === 0 && start.minute() === 0);
      const isEndAllDay = end ? time.isEndAllDay === true : false;

      return {
        start,
        end,
        startDay: start.startOf('day'),
        endDay: (end || start).startOf('day'),
        isAllDay,
        isEndAllDay,
        sourceTime: time,
      };
    })
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());
}

export function buildDayGridEvents(
  event: VisitorEvent,
  activeRange?: CalendarEventActiveRange | null
): CalendarEventInput[] {
  const occurrences = getRenderableOccurrences(event, activeRange);
  if (!occurrences.length) return [];

  const allDayOccurrences = occurrences.filter(({ occurrence }) => occurrence.isAllDay);
  const timedOccurrences = occurrences.filter(({ occurrence }) => !occurrence.isAllDay);

  const allDayEvents = groupContinuousOccurrences(allDayOccurrences).map((segment, index) => {
    const first = segment.occurrences[0];
    const last = segment.occurrences[segment.occurrences.length - 1];
    const spansMultipleDays = segment.endDay.diff(segment.startDay, 'day') > 0;

    return {
      id: `${event._id || event.id || event.name}-all-day-${index}`,
      title: event.name || event.title,
      start: formatCalendarAllDayDate(segment.startDay),
      end: spansMultipleDays
        ? formatCalendarAllDayDate(segment.endDay.add(1, 'day'))
        : (last.end || first.end)?.format('YYYY-MM-DD'),
      allDay: true,
      extendedProps: {
        sourceEvent: event,
        segmentIndex: index,
        isGroupedSpan: segment.occurrences.length > 1 || spansMultipleDays,
      },
    } as CalendarEventInput;
  });

  const timedEvents = timedOccurrences.flatMap(({ occurrence, occurrenceIndex }) =>
    buildTimedDayGridEvents(event, occurrence, occurrenceIndex)
  );

  return [...allDayEvents, ...timedEvents].sort(
    (a, b) => dayjs(a.start as string | Date).valueOf() - dayjs(b.start as string | Date).valueOf()
  );
}

export function buildOccurrenceEvents(
  event: VisitorEvent,
  activeRange?: CalendarEventActiveRange | null
): CalendarEventInput[] {
  return getRenderableOccurrences(event, activeRange).map(({ occurrence, occurrenceIndex }) => {
    const output: CalendarEventInput = {
      id: `${event._id || event.id || event.name}-occurrence-${occurrenceIndex}`,
      title: event.name || event.title,
      start: occurrence.isAllDay
        ? formatCalendarAllDayDate(occurrence.startDay)
        : formatCalendarTimedDateTime(occurrence.start),
      allDay: occurrence.isAllDay,
      extendedProps: {
        sourceEvent: event,
        occurrenceIndex,
      },
    };

    if (occurrence.end) {
      if (occurrence.isAllDay) {
        output.end = formatCalendarAllDayDate(occurrence.endDay.add(1, 'day'));
      } else {
        const formattedEnd = formatCalendarTimedDateTime(occurrence.end);
        // Omit end when it equals start so FullCalendar applies its default
        // duration (1 hour) instead of rendering a zero-height event
        if (formattedEnd !== output.start) {
          output.end = formattedEnd;
        }
      }
    }

    return output;
  });
}

export function groupContinuousOccurrences(
  occurrences: Array<CalendarEventOccurrence | IndexedCalendarEventOccurrence>
): CalendarEventSegment[] {
  if (!occurrences.length) return [];

  const normalizedOccurrences = occurrences.map((occurrence) =>
    isIndexedOccurrence(occurrence) ? occurrence.occurrence : occurrence
  );
  const segments: CalendarEventSegment[] = [];
  let current: CalendarEventSegment = {
    occurrences: [normalizedOccurrences[0]],
    startDay: normalizedOccurrences[0].startDay,
    endDay: normalizedOccurrences[0].endDay,
  };

  for (let index = 1; index < normalizedOccurrences.length; index++) {
    const occurrence = normalizedOccurrences[index];
    const previousEndPlusOneDay = current.endDay.add(1, 'day');
    const isContinuous =
      occurrence.startDay.isSame(current.endDay, 'day') ||
      occurrence.startDay.isBefore(previousEndPlusOneDay) ||
      occurrence.startDay.isSame(previousEndPlusOneDay, 'day');

    if (isContinuous) {
      current.occurrences.push(occurrence);
      if (occurrence.endDay.isAfter(current.endDay)) {
        current.endDay = occurrence.endDay;
      }
      continue;
    }

    segments.push(current);
    current = {
      occurrences: [occurrence],
      startDay: occurrence.startDay,
      endDay: occurrence.endDay,
    };
  }

  segments.push(current);
  return segments;
}

function buildTimedDayGridEvents(
  event: VisitorEvent,
  occurrence: CalendarEventOccurrence,
  occurrenceIndex: number
): CalendarEventInput[] {
  const title = event.name || event.title;
  const baseId = `${event._id || event.id || event.name}-occurrence-${occurrenceIndex}`;
  const spanDays = occurrence.endDay.diff(occurrence.startDay, 'day');

  if (spanDays <= 0) {
    return [
      {
        id: baseId,
        title,
        start: formatCalendarTimedDateTime(occurrence.start),
        end: occurrence.end && !occurrence.end.isSame(occurrence.start)
          ? formatCalendarTimedDateTime(occurrence.end)
          : undefined,
        allDay: false,
        extendedProps: {
          sourceEvent: event,
          occurrenceIndex,
          dayGridTimeText: formatTimedDayGridTimeText(occurrence),
        },
      },
    ];
  }

  return [
    {
      id: `${baseId}-span`,
      title,
      start: formatCalendarAllDayDate(occurrence.startDay),
      end: formatCalendarAllDayDate(occurrence.endDay.add(1, 'day')),
      allDay: true,
      extendedProps: {
        sourceEvent: event,
        occurrenceIndex,
        isGroupedSpan: true,
      },
    },
  ];
}

function isDayGridView(viewName: string) {
  return viewName.startsWith('dayGrid');
}

function getRenderableOccurrences(
  event: VisitorEvent,
  activeRange?: CalendarEventActiveRange | null
): IndexedCalendarEventOccurrence[] {
  const timezoneFormatter = getOccurrenceTimezone(event);

  return buildOccurrences(event)
    .map((occurrence, occurrenceIndex) => ({
      occurrence: activeRange
        ? clipOccurrenceToRange(occurrence, activeRange, timezoneFormatter)
        : occurrence,
      occurrenceIndex,
    }))
    .filter(
      (
        occurrence
      ): occurrence is {
        occurrence: CalendarEventOccurrence;
        occurrenceIndex: number;
      } => !!occurrence.occurrence
    );
}

function clipOccurrenceToRange(
  occurrence: CalendarEventOccurrence,
  activeRange: CalendarEventActiveRange,
  timezoneFormatter: string
) {
  const rangeStart = dayjs(activeRange.from).tz(timezoneFormatter);
  const rangeEnd = dayjs(activeRange.to).tz(timezoneFormatter);
  const occurrenceEnd = occurrence.end || occurrence.start;

  if (occurrenceEnd.isBefore(rangeStart) || occurrence.start.isAfter(rangeEnd)) {
    return null;
  }

  const clippedStart = occurrence.start.isBefore(rangeStart) ? rangeStart : occurrence.start;
  const clippedEnd = occurrence.end
    ? occurrence.end.isAfter(rangeEnd)
      ? rangeEnd
      : occurrence.end
    : null;
  const effectiveEnd = clippedEnd || clippedStart;

  return {
    ...occurrence,
    start: clippedStart,
    end: clippedEnd,
    startDay: clippedStart.startOf('day'),
    endDay: effectiveEnd.startOf('day'),
  };
}

function isIndexedOccurrence(
  occurrence: CalendarEventOccurrence | IndexedCalendarEventOccurrence
): occurrence is IndexedCalendarEventOccurrence {
  return 'occurrence' in occurrence;
}

function formatTimedDayGridTimeText(occurrence: CalendarEventOccurrence) {
  const startText = occurrence.start.format('h:mm A');
  const timezone = occurrence.start.format('z');

  if (!occurrence.end || occurrence.endDay.diff(occurrence.startDay, 'day') > 0) {
    return `${startText} (${timezone})`;
  }

  const endText = occurrence.end.format('h:mm A');
  // Always show range format with timezone, matching event detail page format
  return `${startText} - ${endText} (${timezone})`;
}
